import React, { useMemo, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import createFitisticTheme from './theme/createFitisticTheme';
import CssBaseline from '@mui/material/CssBaseline';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import LandingPage from './pages/LandingPage';
import Pricing from './pages/Pricing';

import AdminGuide from './pages/AdminGuide';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Batches from './pages/Batches';
import UserAnalytics from './pages/UserAnalytics';
import Gyms from './pages/Gyms';
import GymDetail from './pages/GymDetail';
import Exercises from './pages/Exercises';
import Goals from './pages/Goals';
import Notifications from './pages/Notifications';
import TrainerPlans from './pages/TrainerPlans';
import Trainers from './pages/Trainers';
import WorkoutPlans from './pages/WorkoutPlans';
import MealPlans from './pages/MealPlans';
import StaffManagement from './pages/StaffManagement';
import Attendance from './pages/Attendance';
import FoodItems from './pages/FoodItems';
import Feedbacks from './pages/Feedbacks';
import DatabaseManagement from './pages/DatabaseManagement';
import AuditLog from './pages/AuditLog';
import BodyMeasurements from './pages/BodyMeasurements';
import Payments from './pages/Payments';
import GymUpiPayments from './pages/GymUpiPayments';
import Invoices from './pages/Invoices';
import GymInvoices from './pages/GymInvoices';
import InvoiceSettings from './pages/InvoiceSettings';
import GymSettings from './pages/GymSettings';
import Integrations from './pages/Integrations';
import SlotManagement from './pages/SlotManagement';
import Retention from './pages/Retention';
import Leads from './pages/Leads';
import StressContent from './pages/StressContent';
import YogaContent from './pages/YogaContent';
import Marketing from './pages/Marketing';
import NotificationTemplates from './pages/NotificationTemplates';
import Banners from './pages/Banners';
import ChallengesEvents from './pages/ChallengesEvents';
import POS from './pages/POS';
import CheckInKiosk from './pages/CheckInKiosk';
import SocialOperations from './pages/SocialOperations';
import BiometricIntegration from './pages/BiometricIntegration';


import Layout from './components/Layout';
import RequireRole from './components/RequireRole';
import { AppDataProvider, useAppData } from './context/AppDataContext';
import AccessibilityProvider from './components/AccessibilityProvider';
import { GYM_MODULES, userCanAccessGymModule } from './utils/gymModules';

import { ColorModeContext } from './context/ThemeContext';

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

const RequireGymModule = ({ moduleKey, children }) => {
  const { gyms, gymsLoading } = useAppData();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  if (user?.role === 'superadmin' || !moduleKey) return children;
  if (gymsLoading) return null;
  return userCanAccessGymModule(user, gyms, moduleKey) ? children : <Navigate to="/dashboard" replace />;
};

const RequireDefaultGymAdmin = ({ children }) => {
  const { gyms, gymsLoading } = useAppData();
  const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();

  if (user?.role === 'superadmin') return children;
  if (gymsLoading) return null;

  const userGymId = typeof user?.gymId === 'string' ? user.gymId : (user?.gymId?._id || user?.gymId?.id || '');
  const defaultGym = gyms.find(gym => gym.isDefault);
  const defaultGymId = defaultGym?._id || defaultGym?.id || '';
  return user?.role === 'admin' && userGymId && userGymId === defaultGymId
    ? children
    : <Navigate to="/dashboard" replace />;
};

function App() {
  const [mode, setMode] = useState('light');
  
  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
      mode,
    }),
    [mode],
  );

  const theme = useMemo(() => createFitisticTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppDataProvider>
      <AccessibilityProvider>
      <BrowserRouter>
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/admin-guide" element={<AdminGuide />} />
            <Route path="/kiosk" element={
              <PrivateRoute>
                <RequireRole roles={['superadmin', 'gym_admin', 'admin', 'manager', 'front_desk']}>
                  <RequireGymModule moduleKey="attendance"><CheckInKiosk /></RequireGymModule>
                </RequireRole>
              </PrivateRoute>
            } />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="users" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach', 'sales_staff', 'front_desk']}><Users /></RequireRole>} />
              <Route path="batches" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach']}><Batches /></RequireRole>} />
              <Route path="users/:userId/analytics" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach']}><UserAnalytics /></RequireRole>} />
              <Route path="users/:userId/measurements" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'trainer', 'manager', 'coach']}><BodyMeasurements /></RequireRole>} />
              <Route path="staff" element={<RequireRole roles={['superadmin', 'admin', 'gym_admin']}><StaffManagement /></RequireRole>} />
              <Route path="marketing" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'manager', 'sales_staff', 'trainer', 'coach']}><Marketing /></RequireRole>} />
              <Route path="banners" element={<RequireRole roles={['superadmin', 'gym_admin', 'admin', 'manager']}><Banners /></RequireRole>} />
              <Route path="gyms" element={<RequireRole roles={['superadmin']}><Gyms /></RequireRole>} />
              <Route path="gyms/:id" element={<RequireRole roles={['superadmin', 'gym_admin']}><GymDetail /></RequireRole>} />
              <Route path="exercises" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><Exercises /></RequireRole>} />
              <Route path="workout-plans" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><WorkoutPlans /></RequireRole>} />
              <Route path="meal-plans" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><MealPlans /></RequireRole>} />
              <Route path="food-items" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><FoodItems /></RequireRole>} />
              <Route path="trainer-plans" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><TrainerPlans /></RequireRole>} />
              <Route path="trainers" element={<RequireGymModule moduleKey={GYM_MODULES.trainerStaff}><Trainers /></RequireGymModule>} />
              <Route path="yoga-content" element={<RequireDefaultGymAdmin><YogaContent /></RequireDefaultGymAdmin>} />
              <Route path="stress-content" element={<RequireDefaultGymAdmin><StressContent /></RequireDefaultGymAdmin>} />
              <Route path="goals" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager']}><Goals /></RequireRole>} />
              <Route path="notifications" element={<Navigate to="/dashboard/marketing" replace />} />
              <Route path="attendance" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><Attendance /></RequireRole>} />
              <Route path="slot-management" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><RequireGymModule moduleKey={GYM_MODULES.slotBooking}><SlotManagement /></RequireGymModule></RequireRole>} />
              <Route path="challenges-events" element={<RequireRole roles={['superadmin','gym_admin','admin']}><ChallengesEvents /></RequireRole>} />
              <Route path="database" element={<RequireRole roles={['superadmin']}><DatabaseManagement /></RequireRole>} />
              <Route path="feedback" element={<RequireRole roles={['superadmin','gym_admin','admin','trainer','coach','manager','front_desk']}><Feedbacks /></RequireRole>} />
              <Route path="payments" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><Payments /></RequireRole>} />
              <Route path="gym-upi-payments" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.invoice}><GymUpiPayments /></RequireGymModule></RequireRole>} />
              <Route path="gym-invoices" element={<RequireRole roles={[ 'superadmin' ]}><GymInvoices /></RequireRole>} />
              <Route path="invoices" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.invoice}><Invoices /></RequireGymModule></RequireRole>} />
              <Route path="invoice-settings" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.invoice}><InvoiceSettings /></RequireGymModule></RequireRole>} />
              <Route path="gym-settings" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><GymSettings /></RequireRole>} />
              <Route path="notification-templates" element={<RequireRole roles={['superadmin','gym_admin','admin']}><NotificationTemplates /></RequireRole>} />
              <Route path="integrations" element={<RequireRole roles={['superadmin','gym_admin','admin']}><Integrations /></RequireRole>} />
              <Route path="audit" element={<RequireRole roles={[ 'superadmin', 'admin' ]}><AuditLog /></RequireRole>} />
              <Route path="retention" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><RequireGymModule moduleKey={GYM_MODULES.memberRetention}><Retention /></RequireGymModule></RequireRole>} />
              <Route path="leads" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><Leads /></RequireRole>} />
              <Route path="social-operations" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff']}><SocialOperations /></RequireRole>} />
              <Route path="biometric" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','front_desk']}><BiometricIntegration /></RequireRole>} />
              <Route path="pos" element={<RequireRole roles={['superadmin','gym_admin','admin','manager','sales_staff','front_desk']}><POS /></RequireRole>} />

            </Route>
        </Routes>
      </BrowserRouter>
      </AccessibilityProvider>
      </AppDataProvider>
    </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
